import { AlertsFormState } from 'modules/campaigns/alerts/types';
import { AlertTakeoverCampaign } from 'modules/campaigns/lib/takeovers/AlertTakeover/types';
import { FC } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Link } from 'ui/links';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button, GhostButton } from 'ui/button';
import { Reset24 } from 'ui/icons';
import { H3, body2, colors, spacing, zIndex } from 'ui/theme';
import {
  BodyHeader,
  BodyWrapper,
  FooterWrapper,
  TakeoverHeaderBarContent,
  WithdrawnDrawerTakeover,
} from '../../../editor/takeovers/Takeovers.styled';
import { OverrideAlertOptions } from './OverrideAlertOptions';
import { useAlertOverridesContext } from './context/AlertOverridesContext';

const TakeoverDescription = styled.div`
  ${body2}
  color: ${colors.secondaryText};
  padding: ${spacing.base} 0 ${spacing.x3} 0;
`;

const AlertOverridesTakeover = styled(WithdrawnDrawerTakeover)`
  z-index: ${zIndex.modal};
`;

export type AlertOverridesProps = {
  campaignData: AlertTakeoverCampaign;
  onTakeoverOpen: (isOpen: boolean) => void;
};

export const ALERT_OVERRIDES_SAVE_ID = 'alert-overrides-save-button';
export const RESET_TO_PROJECT_DEFAULTS_ID = 'reset-to-project-defaults';

export const AlertOverrides: FC<AlertOverridesProps> = ({ campaignData, onTakeoverOpen }) => {
  const { projectDefaultValues } = useAlertOverridesContext();
  const { handleSubmit, reset } = useFormContext();

  const handleUpdate: SubmitHandler<AlertsFormState> = (values) => {
    // TODO: integrate upsertCampaignAlerts mutation here when available
    console.log({ values });
  };

  return (
    <AlertOverridesTakeover
      isOverlay
      withPortal
      onClose={() => onTakeoverOpen(false)}
      HeaderBarContent={<TakeoverHeaderBarContent>Alerts</TakeoverHeaderBarContent>}
      Body={
        <BodyWrapper>
          <BodyHeader>
            <H3>Campaign alert settings</H3>
            <GhostButton
              data-test={RESET_TO_PROJECT_DEFAULTS_ID}
              onClick={() => reset(projectDefaultValues)}
              size={BUTTON_SIZES.LARGE}
            >
              <Reset24 /> Reset to project defaults
            </GhostButton>
          </BodyHeader>
          <TakeoverDescription>
            Configure notification preferences for the {campaignData.name} campaign. Modifying these
            settings will not affect the{' '}
            <Link hasOpenNewTabIcon href="/settings/alerts/campaigns" target="_blank">
              default alert settings
            </Link>{' '}
            for this project.
          </TakeoverDescription>
          <OverrideAlertOptions campaignData={campaignData} />
        </BodyWrapper>
      }
      Footer={
        <FooterWrapper>
          <Button
            data-test={ALERT_OVERRIDES_SAVE_ID}
            variant={BUTTON_VARIANTS.PRIMARY}
            onClick={handleSubmit(handleUpdate)}
          >
            Save
          </Button>
        </FooterWrapper>
      }
    />
  );
};
